import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Alert, ConfirmDialog, Modal, Spinner } from '../components/ui.tsx'
import { api, type Role, type User } from '../lib/api.ts'
import { useAuth } from '../lib/auth.tsx'
import { useAction, useAsync } from '../lib/hooks.ts'
import { formatDate } from '../lib/format.ts'

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrátor',
  coach: 'Trenér',
  client: 'Klient',
}

/** Správa všech účtů. Dostupné jen administrátorovi. */
export function UsersPage(): React.JSX.Element {
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<User | null>(null)

  const users = useAsync(() => api.users.list(), [])
  const list = users.data?.users ?? []

  return (
    <main className="page">
      <div className="page__header">
        <div>
          <h1>Uživatelé</h1>
          <p>Všechny účty v systému — administrátoři, trenéři i klienti.</p>
        </div>
        <div className="page__actions">
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            Nový účet
          </button>
        </div>
      </div>

      {users.error && <Alert>{users.error}</Alert>}
      {users.loading && <Spinner />}

      {list.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Jméno</th>
                <th>E-mail</th>
                <th>Role</th>
                <th>Trenér</th>
                <th>Stav</th>
                <th>Založen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.role === 'client' ? (
                      <Link to={`/klienti/${user.id}`}>{user.name}</Link>
                    ) : (
                      user.name
                    )}
                  </td>
                  <td className="muted">{user.email}</td>
                  <td>
                    <span className={`badge ${user.role === 'admin' ? 'badge--accent' : ''}`}>
                      {ROLE_LABELS[user.role]}
                    </span>
                  </td>
                  <td className="muted">
                    {user.coachId ? (list.find((c) => c.id === user.coachId)?.name ?? '—') : '—'}
                  </td>
                  <td>
                    <span className={`badge ${user.active ? 'badge--success' : 'badge--danger'}`}>
                      {user.active ? 'aktivní' : 'vypnutý'}
                    </span>
                  </td>
                  <td className="muted">{formatDate(user.createdAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setEditing(user)}
                    >
                      Upravit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <NewUserModal
          coaches={list.filter((u) => u.role === 'coach' || u.role === 'admin')}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            users.reload()
          }}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          coaches={list.filter((u) => u.role === 'coach' || u.role === 'admin')}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            users.reload()
          }}
        />
      )}
    </main>
  )
}

function NewUserModal({
  coaches,
  onClose,
  onCreated,
}: {
  coaches: User[]
  onClose: () => void
  onCreated: () => void
}): React.JSX.Element {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<Role>('coach')
  const [coachId, setCoachId] = useState<number | ''>('')

  const create = useAction(api.users.create)

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    const result = await create.run({
      name,
      email,
      password,
      role,
      coachId: role === 'client' && coachId !== '' ? coachId : null,
    })
    if (result) onCreated()
  }

  return (
    <Modal
      title="Nový účet"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Zrušit
          </button>
          <button type="submit" form="new-user" className="btn btn--primary" disabled={create.pending}>
            {create.pending ? 'Zakládám…' : 'Založit'}
          </button>
        </>
      }
    >
      <form id="new-user" onSubmit={(e) => void submit(e)}>
        {create.error && <Alert>{create.error}</Alert>}

        <label className="field">
          <span className="field__label">Jméno</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>

        <label className="field">
          <span className="field__label">E-mail</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>

        <label className="field">
          <span className="field__label">Heslo</span>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
        </label>

        <label className="field">
          <span className="field__label">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="coach">Trenér</option>
            <option value="client">Klient</option>
            <option value="admin">Administrátor</option>
          </select>
        </label>

        {role === 'client' && (
          <label className="field">
            <span className="field__label">Přiřadit trenérovi</span>
            <select value={coachId} onChange={(e) => setCoachId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— bez trenéra —</option>
              {coaches.map((coach) => (
                <option key={coach.id} value={coach.id}>
                  {coach.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </form>
    </Modal>
  )
}

function EditUserModal({
  user,
  coaches,
  onClose,
  onSaved,
}: {
  user: User
  coaches: User[]
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const { user: me } = useAuth()
  const [name, setName] = useState(user.name)
  const [email, setEmail] = useState(user.email)
  const [role, setRole] = useState<Role>(user.role)
  const [coachId, setCoachId] = useState<number | ''>(user.coachId ?? '')
  const [active, setActive] = useState(user.active)
  const [deleting, setDeleting] = useState(false)

  const save = useAction(() =>
    api.users.update(user.id, {
      name,
      email,
      role,
      coachId: role === 'client' && coachId !== '' ? coachId : null,
      active,
    }),
  )
  const remove = useAction(() => api.users.remove(user.id))

  const isSelf = me?.id === user.id

  return (
    <Modal
      title={`Účet — ${user.name}`}
      onClose={onClose}
      footer={
        <>
          {!isSelf && (
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => setDeleting(true)}
              style={{ marginRight: 'auto' }}
            >
              Smazat
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            Zrušit
          </button>
          <button type="submit" form="edit-user" className="btn btn--primary" disabled={save.pending}>
            {save.pending ? 'Ukládám…' : 'Uložit'}
          </button>
        </>
      }
    >
      <form
        id="edit-user"
        onSubmit={(e) => {
          e.preventDefault()
          void save.run().then((result) => result && onSaved())
        }}
      >
        {save.error && <Alert>{save.error}</Alert>}

        <label className="field">
          <span className="field__label">Jméno</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </label>

        <label className="field">
          <span className="field__label">E-mail</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>

        <label className="field">
          <span className="field__label">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as Role)} disabled={isSelf}>
            <option value="coach">Trenér</option>
            <option value="client">Klient</option>
            <option value="admin">Administrátor</option>
          </select>
          {isSelf && <span className="field__help">Vlastní roli si změnit nelze.</span>}
        </label>

        {role === 'client' && (
          <label className="field">
            <span className="field__label">Trenér</span>
            <select value={coachId} onChange={(e) => setCoachId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— bez trenéra —</option>
              {coaches
                .filter((c) => c.id !== user.id)
                .map((coach) => (
                  <option key={coach.id} value={coach.id}>
                    {coach.name}
                  </option>
                ))}
            </select>
          </label>
        )}

        <div className="field field--inline">
          <input
            id="user-active"
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            disabled={isSelf}
          />
          <label htmlFor="user-active" className="field__label" style={{ cursor: 'pointer' }}>
            Účet je aktivní
          </label>
        </div>
        <p className="field__help">
          Deaktivací se uživatel okamžitě odhlásí a nemůže se přihlásit. Data mu zůstanou.
        </p>
      </form>

      {deleting && (
        <ConfirmDialog
          title="Smazat účet"
          message={
            <>
              Opravdu nenávratně smazat účet <strong>{user.name}</strong> včetně celé tréninkové
              historie? Ve většině případů stačí účet jen deaktivovat.
            </>
          }
          confirmLabel="Smazat nenávratně"
          danger
          pending={remove.pending}
          onCancel={() => setDeleting(false)}
          onConfirm={() => void remove.run().then(onSaved)}
        />
      )}
    </Modal>
  )
}
